import { format } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'
import type { Locale } from 'date-fns'
import enUS from 'date-fns/locale/en-US'
import es from 'date-fns/locale/es'
import type { AppLocale } from '@/core/config/i18n/resolveDeviceLocale'

const DATE_FNS_LOCALES: Record<AppLocale, Locale> = {
  es,
  en: enUS,
}

export const getDateFnsLocale = (appLocale: AppLocale): Locale =>
  DATE_FNS_LOCALES[appLocale] ?? enUS

export const formatWithAppLocale = (
  date: Date,
  formatStr: string,
  appLocale: AppLocale,
): string => format(date, formatStr, { locale: getDateFnsLocale(appLocale) })

const parseInstant = (value: string): Date | null => {
  const date = new Date(value.trim().replace(' ', 'T'))

  if (Number.isNaN(date.getTime())) {
    return null
  }

  return date
}

/** Día de calendario local a partir de un Y-m-d o instante ISO. */
export function toLocalCalendarDate(
  value: string | null | undefined,
): Date | null {
  if (!value) {
    return null
  }

  const trimmed = value.trim()

  // Fecha solo-día: nunca parsear como UTC midnight (rompe zonas UTC−N).
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed)
  if (dateOnly) {
    const year = Number(dateOnly[1])
    const month = Number(dateOnly[2]) - 1
    const day = Number(dateOnly[3])
    const local = new Date(year, month, day)

    if (
      Number.isNaN(local.getTime()) ||
      local.getFullYear() !== year ||
      local.getMonth() !== month ||
      local.getDate() !== day
    ) {
      return null
    }

    return local
  }

  const instant = parseInstant(trimmed)

  if (!instant) {
    return null
  }

  return new Date(instant.getFullYear(), instant.getMonth(), instant.getDate())
}

/** Para DatePicker de solo fecha: interpreta Y-m-d o instante en zona local. */
export function parseCalendarDateFromApi(
  value: string | null | undefined,
): Date | null {
  return toLocalCalendarDate(value)
}

/** Para DatePicker con hora: instante completo en zona local. */
export function parseDatetimeFromApi(
  value: string | null | undefined,
): Date | null {
  if (!value) {
    return null
  }

  return parseInstant(value)
}

export function startOfLocalDay(date: Date): Date {
  const result = new Date(date)
  result.setHours(0, 0, 0, 0)
  return result
}

export function endOfLocalDay(date: Date): Date {
  const result = new Date(date)
  result.setHours(23, 59, 59, 0)
  return result
}

/** Redondea hacia arriba al siguiente bloque de minutos (p. ej. 18:07 → 18:15). */
export function roundLocalDateUp(date: Date, intervalMinutes = 15): Date {
  const result = new Date(date)
  result.setSeconds(0, 0)
  result.setMilliseconds(0)

  const totalMinutes = result.getHours() * 60 + result.getMinutes()
  const remainder = totalMinutes % intervalMinutes

  if (remainder !== 0) {
    result.setMinutes(result.getMinutes() + (intervalMinutes - remainder))
  }

  return result
}

/**
 * Valores por defecto al crear un evento:
 * - Hoy: ahora redondeado + duración.
 * - Día futuro: misma hora del día actual en esa fecha.
 * - Día pasado: 09:00 (caso poco frecuente).
 */
export function buildDefaultEventRange(
  anchorDay?: Date | null,
  durationMinutes = 60,
): { start: Date; end: Date } {
  const now = new Date()
  const today = startOfLocalDay(now)
  const targetDay = startOfLocalDay(anchorDay ?? now)

  let start: Date

  if (targetDay.getTime() < today.getTime()) {
    start = new Date(targetDay)
    start.setHours(9, 0, 0, 0)
  } else if (targetDay.getTime() === today.getTime()) {
    start = roundLocalDateUp(now)
  } else {
    start = new Date(targetDay)
    start.setHours(now.getHours(), now.getMinutes(), 0, 0)
    start = roundLocalDateUp(start)
  }

  const end = new Date(start)
  end.setMinutes(end.getMinutes() + durationMinutes)

  return { start, end }
}

/** ISO 8601 con offset de la zona horaria del navegador. */
export function formatLocalIso(date: Date): string {
  const pad = (n: number) => n.toString().padStart(2, '0')
  const Y = date.getFullYear()
  const M = pad(date.getMonth() + 1)
  const D = pad(date.getDate())
  const h = pad(date.getHours())
  const m = pad(date.getMinutes())
  const s = pad(date.getSeconds())

  const tzo = -date.getTimezoneOffset()
  const dif = tzo >= 0 ? '+' : '-'
  const tzoH = pad(Math.floor(Math.abs(tzo) / 60))
  const tzoM = pad(Math.abs(tzo) % 60)

  return `${Y}-${M}-${D}T${h}:${m}:${s}${dif}${tzoH}:${tzoM}`
}

/** Solo fecha de calendario (Y-m-d), sin hora ni offset — evita saltos UTC. */
export function formatCalendarDateForApi(date: Date): string {
  const local = startOfLocalDay(date)
  const pad = (n: number) => n.toString().padStart(2, '0')

  return `${local.getFullYear()}-${pad(local.getMonth() + 1)}-${pad(local.getDate())}`
}

/** Suma días a una fecha Y-m-d y devuelve otra Y-m-d en calendario local. */
export function addDaysToCalendarDate(value: string, days: number): string {
  const date = parseCalendarDateFromApi(value)

  if (!date) {
    return value
  }

  date.setDate(date.getDate() + days)

  return formatCalendarDateForApi(date)
}

/** Convierte instante API (UTC) → ISO local listo para reenviar al backend. */
export function apiInstantToLocalIso(
  value: string | null | undefined,
): string | null {
  if (!value) {
    return null
  }

  const instant = parseInstant(value)

  if (!instant) {
    return null
  }

  return formatLocalIso(instant)
}

export function calendarDatesEqual(
  a: string | null | undefined,
  b: string | null | undefined,
): boolean {
  const dateA = toLocalCalendarDate(a)
  const dateB = toLocalCalendarDate(b)

  if (!dateA && !dateB) {
    return true
  }

  if (!dateA || !dateB) {
    return false
  }

  return dateA.getTime() === dateB.getTime()
}

export function instantsEqual(
  a: string | null | undefined,
  b: string | null | undefined,
): boolean {
  if (!a && !b) {
    return true
  }

  if (!a || !b) {
    return false
  }

  const dateA = parseInstant(a)
  const dateB = parseInstant(b)

  if (!dateA || !dateB) {
    return false
  }

  return dateA.getTime() === dateB.getTime()
}

export function formatCalendarDateDisplay(
  value: string | null | undefined,
  formatStr = 'MM/dd/yyyy',
): string {
  const date = toLocalCalendarDate(value)

  if (!date) {
    return ''
  }

  return format(date, formatStr)
}

export function formatDatetimeDisplay(
  value: string | null | undefined,
  formatStr = 'MM/dd/yyyy hh:mm a',
): string {
  if (!value) {
    return ''
  }

  const date = parseInstant(value)

  if (!date) {
    return ''
  }

  return format(date, formatStr)
}

export function parseDatePickerValue(
  value: unknown,
  mode: 'calendar' | 'instant' = 'instant',
): Date | null | Date[] | (Date | null)[] {
  if (!value) {
    return null
  }

  if (Array.isArray(value)) {
    return value.map((entry) =>
      entry
        ? mode === 'calendar'
          ? parseCalendarDateFromApi(String(entry))
          : parseDatetimeFromApi(String(entry))
        : null,
    )
  }

  if (typeof value === 'string') {
    return mode === 'calendar'
      ? parseCalendarDateFromApi(value)
      : parseDatetimeFromApi(value)
  }

  return value as Date
}

/** @deprecated Usar apiInstantToLocalIso */
export const calendarDateFromApiToLocalIso = apiInstantToLocalIso

export const getViewerTimezone = (): string =>
  Intl.DateTimeFormat().resolvedOptions().timeZone

export type EventTimeDualDisplay = {
  localRange: string
  eventRange: string | null
  showEventReference: boolean
}

export const formatEventTimeDual = (
  startsAt: string,
  endsAt: string | null | undefined,
  eventTimezone: string,
  allDay: boolean,
  formatLocal: (date: Date, formatStr: string) => string,
  labels: {
    allDay: string
    allDayInTimezone: (timezone: string) => string
  },
): EventTimeDualDisplay => {
  const viewerTimezone = getViewerTimezone()
  const showEventReference = eventTimezone !== viewerTimezone

  if (allDay) {
    if (!showEventReference) {
      return {
        localRange: labels.allDay,
        eventRange: null,
        showEventReference: false,
      }
    }

    return {
      localRange: labels.allDay,
      eventRange: labels.allDayInTimezone(eventTimezone),
      showEventReference: true,
    }
  }

  const start = parseDatetimeFromApi(startsAt)

  if (!start) {
    return { localRange: '', eventRange: null, showEventReference: false }
  }

  const end = endsAt ? parseDatetimeFromApi(endsAt) : null

  const localRange = end
    ? `${formatLocal(start, 'HH:mm')} — ${formatLocal(end, 'HH:mm')}`
    : formatLocal(start, 'HH:mm')

  if (!showEventReference) {
    return { localRange, eventRange: null, showEventReference: false }
  }

  const formatInEventTz = (date: Date) =>
    formatInTimeZone(date, eventTimezone, 'HH:mm zzz')

  const eventRange = end
    ? `${formatInEventTz(start)} — ${formatInEventTz(end)}`
    : formatInEventTz(start)

  return { localRange, eventRange, showEventReference: true }
}
